import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import {
  setUnauthorizedHandler,
  authApi,
  webauthnApi,
  setAccessToken,
  getAccessToken,
  migrateAccessTokenFromStorage,
  clearLegacyAccessTokenStorage,
  setRefreshBlocked,
  ApiException,
} from '@/lib/api';
import {
  deviceUnlockDb,
  syncQueueDb,
  wipeLocalVaultAndData,
  metadataDb,
  type DeviceUnlockRecord,
} from '@/lib/db';
import { isStandaloneDisplay } from '@/lib/pwa';
import {
  assertLocalUnlock,
  assertPlatformAuthenticator,
  enrollPlatformAuthenticator,
  getAuthenticatorLabel,
  isPlatformAuthenticatorAvailable,
} from '@/lib/webauthn';
import {
  generateDek,
  setMemoryDek,
  wipeMemoryDek,
  wrapDek,
  unwrapDek,
  clearVaultBlobs,
} from '@/lib/vault';
import type { UserProfile } from '@/types';

const BACKGROUND_LOCK_MS = 45_000;
const FOREGROUND_IDLE_LOCK_MS = 5 * 60_000;
const ELEVATED_TTL_MS = 5 * 60_000;
const ENROLL_NUDGE_DISMISS_KEY = 'faceIdEnrollNudgeDismissed';

export type AuthSessionState = 'locked' | 'localUnlocked' | 'onlineAuthenticated';

interface User {
  id: string;
  email: string;
  username: string;
  displayName?: string;
  givenName?: string;
  picture?: string;
  name?: string;
  provider?: string;
  providerManaged: boolean;
  isAdmin: boolean;
}

function mapMeToUser(me: UserProfile & { id?: string }): User {
  return {
    id: me.userId ?? me.id ?? '',
    email: me.email ?? '',
    username: me.username ?? '',
    displayName: me.displayName,
    givenName: me.givenName,
    picture: me.picture,
    name: me.name,
    provider: me.provider,
    providerManaged: me.providerManaged ?? false,
    isAdmin: me.isAdmin ?? false,
  };
}

interface AuthError {
  reason: string;
  message: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authError: AuthError | null;
  clearAuthError: () => void;
  /** locked | localUnlocked | onlineAuthenticated — meaningful when Face ID enrolled on PWA. */
  sessionState: AuthSessionState | null;
  isLocked: boolean;
  isFaceIdEnrolled: boolean;
  faceIdLabel: string;
  canOfflineUnlock: boolean;
  showFaceIdEnrollNudge: boolean;
  dismissFaceIdEnrollNudge: () => void;
  loginWithCognito: () => void;
  logout: () => Promise<void>;
  getAccessToken: () => string | null;
  handleTokenExpiration: (reason?: string, message?: string) => void;
  refreshUser: () => Promise<void>;
  lockSession: (message?: string) => void;
  unlockWithFaceId: () => Promise<void>;
  enrollFaceId: () => Promise<{ prfEnabled: boolean }>;
  disableFaceId: () => Promise<void>;
  /** Sensitive settings: Face ID (PWA online) or Google session (web). Offline → false. */
  requireElevatedAuth: () => Promise<{ ok: boolean; message?: string }>;
  isElevated: boolean;
  lockMessage: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<AuthError | null>(null);
  const [sessionState, setSessionState] = useState<AuthSessionState | null>(null);
  const [isFaceIdEnrolled, setIsFaceIdEnrolled] = useState(false);
  const [canOfflineUnlock, setCanOfflineUnlock] = useState(false);
  const [showFaceIdEnrollNudge, setShowFaceIdEnrollNudge] = useState(false);
  const [lockMessage, setLockMessage] = useState<string | null>(null);
  const [elevatedUntil, setElevatedUntil] = useState(0);
  const [faceIdLabel] = useState(() => getAuthenticatorLabel());

  const hiddenAtRef = useRef<number | null>(null);
  const idleTimerRef = useRef<number | null>(null);
  const deviceUnlockRef = useRef<DeviceUnlockRecord | null>(null);

  const wipeSecrets = useCallback(() => {
    wipeMemoryDek();
    void import('@/lib/sealedOffline').then(({ wipeSealedCache }) => wipeSealedCache());
    setAccessToken(null);
    clearLegacyAccessTokenStorage();
  }, []);

  const loadUserFromToken = useCallback(async (_token: string) => {
    try {
      const me = await authApi.getMe();
      const mapped = mapMeToUser(me);
      setUser(mapped);
      return mapped;
    } catch {
      const token = getAccessToken();
      if (!token) return null;
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]!));
        const groups: string[] = Array.isArray(payload['cognito:groups'])
          ? payload['cognito:groups']
          : [];
        const mapped: User = {
          id: payload.sub,
          email: payload.email,
          username: payload['cognito:username'] || payload.preferred_username || payload.email,
          displayName: payload.name || payload.given_name,
          givenName: payload.given_name,
          picture: payload.picture,
          provider: undefined,
          providerManaged: false,
          isAdmin: groups.some((group) => group.toUpperCase() === 'ADMIN'),
        };
        setUser(mapped);
        return mapped;
      }
      return null;
    }
  }, []);

  const applyOnlineSession = useCallback(
    async (
      token: string,
      opts?: {
        elevate?: boolean;
        showEnrollNudge?: boolean;
        userSnapshot?: {
          userId: string;
          email?: string;
          username?: string;
          displayName?: string;
          name?: string;
          provider?: string;
          providerManaged?: boolean;
          isAdmin?: boolean;
        };
      }
    ) => {
      setRefreshBlocked(false);
      setAccessToken(token);
      clearLegacyAccessTokenStorage();
      setSessionState('onlineAuthenticated');
      setLockMessage(null);
      if (opts?.userSnapshot?.userId) {
        setUser({
          id: opts.userSnapshot.userId,
          email: opts.userSnapshot.email ?? '',
          username: opts.userSnapshot.username ?? '',
          displayName: opts.userSnapshot.displayName,
          name: opts.userSnapshot.name,
          provider: opts.userSnapshot.provider,
          providerManaged: opts.userSnapshot.providerManaged ?? false,
          isAdmin: opts.userSnapshot.isAdmin ?? false,
        });
      }
      const mapped = await loadUserFromToken(token);
      if (opts?.elevate) {
        setElevatedUntil(Date.now() + ELEVATED_TTL_MS);
      }
      if (opts?.showEnrollNudge && isStandaloneDisplay() && (mapped || opts.userSnapshot)) {
        const enrolled = await deviceUnlockDb.isEnrolled();
        const dismissed = (await metadataDb.get(ENROLL_NUDGE_DISMISS_KEY)) === '1';
        const platformOk = await isPlatformAuthenticatorAvailable();
        if (!enrolled && !dismissed && platformOk) {
          setShowFaceIdEnrollNudge(true);
        }
      }
    },
    [loadUserFromToken]
  );

  const lockSession = useCallback(
    (message?: string) => {
      wipeSecrets();
      setRefreshBlocked(true);
      setElevatedUntil(0);
      if (message) setLockMessage(message);
      if (deviceUnlockRef.current && isStandaloneDisplay()) {
        setSessionState('locked');
        // Keep lightweight user shell for lock screen greeting when possible
      } else {
        setSessionState(null);
        setUser(null);
      }
    },
    [wipeSecrets]
  );

  const handleRemoteAuthFailure = useCallback(
    async (reason?: string, message?: string) => {
      const code = (reason || '').toLowerCase();
      const isDeleted =
        code.includes('deleted') ||
        code.includes('account_gone') ||
        code.includes('user_not_found') ||
        (message || '').toLowerCase().includes('no longer available');
      const isRevoked =
        code.includes('revoked') ||
        code.includes('credential') ||
        (message || '').toLowerCase().includes('device was removed');

      if (isDeleted) {
        await wipeLocalVaultAndData();
        deviceUnlockRef.current = null;
        setIsFaceIdEnrolled(false);
        setCanOfflineUnlock(false);
        wipeSecrets();
        setUser(null);
        setSessionState(null);
        setAuthError({
          reason: 'account_deleted',
          message: 'This account is no longer available.',
        });
        return;
      }

      if (isRevoked) {
        await wipeLocalVaultAndData();
        deviceUnlockRef.current = null;
        setIsFaceIdEnrolled(false);
        setCanOfflineUnlock(false);
        wipeSecrets();
        setUser(null);
        setSessionState(null);
        setAuthError({
          reason: 'device_revoked',
          message: 'This device was removed. Sign in with Google to continue.',
        });
        return;
      }

      // Enrolled PWA: lock rather than hard logout
      if (deviceUnlockRef.current && isStandaloneDisplay()) {
        lockSession(message || 'Your session expired. Unlock to continue.');
        return;
      }

      wipeSecrets();
      setUser(null);
      setSessionState(null);
      setAuthError({
        reason: reason || 'authentication_failed',
        message: message || 'Authentication failed. Please log in again.',
      });
    },
    [lockSession, wipeSecrets]
  );

  const handleTokenExpiration = useCallback(
    (reason?: string, message?: string) => {
      void handleRemoteAuthFailure(reason, message);
      window.history.replaceState({}, document.title, '/');
    },
    [handleRemoteAuthFailure]
  );

  const refreshDeviceUnlockState = useCallback(async () => {
    const record = await deviceUnlockDb.get();
    deviceUnlockRef.current = record ?? null;
    setIsFaceIdEnrolled(Boolean(record?.credentialId));
    setCanOfflineUnlock(Boolean(record?.prfEnabled && record?.wrappedDek));
    return record ?? null;
  }, []);

  // Boot: migrate token, OAuth session exchange, enrolled → lock on standalone
  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      try {
        setUnauthorizedHandler((reason, message) => {
          handleTokenExpiration(reason, message);
        });

        const record = await refreshDeviceUnlockState();
        const standalone = isStandaloneDisplay();
        const params = new URLSearchParams(window.location.search);
        const sessionCode = params.get('session');
        const legacyToken = params.get('token');
        const error = params.get('error');
        const reason = params.get('reason');
        const message = params.get('message');

        if (reason) {
          handleTokenExpiration(reason, message || undefined);
        }

        if (sessionCode || legacyToken) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        if (error) {
          console.error('OAuth error:', error);
        }

        if (sessionCode) {
          try {
            const exchanged = await authApi.exchangeSession(sessionCode);
            if (cancelled) return;
            await applyOnlineSession(exchanged.accessToken, {
              elevate: true,
              showEnrollNudge: true,
              userSnapshot: exchanged.user,
            });
            return;
          } catch (err) {
            console.error('Session exchange failed:', err);
          }
        } else if (legacyToken) {
          // Temporary compat while backend migrates off ?token=
          if (cancelled) return;
          await applyOnlineSession(legacyToken, { elevate: true, showEnrollNudge: true });
          return;
        }

        // Enrolled installed PWA: always start locked (cold start rule)
        if (record && standalone) {
          wipeSecrets();
          setRefreshBlocked(true);
          setSessionState('locked');
          if (record.userId) {
            setUser((prev) =>
              prev ?? {
                id: record.userId,
                email: '',
                username: '',
                providerManaged: false,
                isAdmin: false,
              }
            );
          }
          return;
        }

        // Browser tab / non-enrolled: try memory migration + cookie refresh
        migrateAccessTokenFromStorage();
        let token = getAccessToken();
        if (!token) {
          try {
            const refreshed = await authApi.refresh();
            token = refreshed.accessToken;
            setAccessToken(token);
          } catch {
            /* no session */
          }
        }

        if (token) {
          await applyOnlineSession(token);
        }
      } catch (err) {
        console.error('Auth boot failed:', err);
        wipeSecrets();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void boot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- boot once
  }, []);

  // Re-lock timers for enrolled PWA
  useEffect(() => {
    if (!isFaceIdEnrolled || !isStandaloneDisplay()) return;
    if (sessionState !== 'onlineAuthenticated' && sessionState !== 'localUnlocked') {
      return;
    }

    const resetIdle = () => {
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = window.setTimeout(() => {
        lockSession();
      }, FOREGROUND_IDLE_LOCK_MS);
    };

    const onActivity = () => resetIdle();
    const events = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const;
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    resetIdle();

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        const hiddenAt = hiddenAtRef.current;
        hiddenAtRef.current = null;
        if (hiddenAt && Date.now() - hiddenAt > BACKGROUND_LOCK_MS) {
          lockSession();
        } else {
          resetIdle();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      events.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isFaceIdEnrolled, sessionState, lockSession]);

  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);

  const loginWithCognito = useCallback(() => {
    const backendUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';
    window.location.href = `${backendUrl}/v1/oauth/login`;
  }, []);

  const confirmWipeIfUnsynced = async (): Promise<boolean> => {
    const pending = await syncQueueDb.count();
    if (pending <= 0) return true;
    return window.confirm(
      'Unsynced changes on this device will be permanently deleted.'
    );
  };

  const logout = useCallback(async () => {
    const enrolled = await deviceUnlockDb.isEnrolled();
    if (enrolled) {
      const ok = await confirmWipeIfUnsynced();
      if (!ok) return;
      await clearVaultBlobs();
      await wipeLocalVaultAndData();
      deviceUnlockRef.current = null;
      setIsFaceIdEnrolled(false);
      setCanOfflineUnlock(false);
    }
    try {
      await authApi.logoutSession();
    } catch {
      /* continue to Cognito logout */
    }
    wipeSecrets();
    setUser(null);
    setSessionState(null);
    const backendUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';
    window.location.href = `${backendUrl}/v1/oauth/logout`;
  }, [wipeSecrets]);

  const unlockWithFaceId = useCallback(async () => {
    const record = deviceUnlockRef.current ?? (await deviceUnlockDb.get());
    if (!record?.credentialId) {
      throw new Error('Face ID is not set up on this device');
    }
    deviceUnlockRef.current = record;

    const online = navigator.onLine;

    if (!online) {
      if (!record.prfEnabled || !record.wrappedDek) {
        setLockMessage(
          'Offline unlock needs a newer device security feature. You can still unlock with Face ID while online.'
        );
        throw new Error('offline_unlock_unavailable');
      }
      const { prfOutput } = await assertLocalUnlock({
        credentialId: record.credentialId,
        enablePrf: true,
      });
      if (!prfOutput) {
        setLockMessage(
          'Offline unlock needs a newer device security feature. You can still unlock with Face ID while online.'
        );
        throw new Error('offline_unlock_unavailable');
      }
      const dek = await unwrapDek(record.wrappedDek, prfOutput);
      setMemoryDek(dek);
      const { hydrateSealedCacheFromVault } = await import('@/lib/sealedOffline');
      await hydrateSealedCacheFromVault();
      setRefreshBlocked(true); // still no online token
      setSessionState('localUnlocked');
      setLockMessage(null);
      setElevatedUntil(0);
      return;
    }

    // Online: assert against server, then set access token
    try {
      const options = await webauthnApi.loginOptions(record.credentialId);
      const { credential, prfOutput } = await assertPlatformAuthenticator(options, {
        enablePrf: Boolean(record.prfEnabled && record.wrappedDek),
        credentialIdFallback: record.credentialId,
      });
      const loggedIn = await webauthnApi.login(credential);
      if (record.prfEnabled && record.wrappedDek && prfOutput) {
        const dek = await unwrapDek(record.wrappedDek, prfOutput);
        setMemoryDek(dek);
        const { hydrateSealedCacheFromVault } = await import('@/lib/sealedOffline');
        await hydrateSealedCacheFromVault();
      }
      await applyOnlineSession(loggedIn.accessToken, {
        elevate: true,
        userSnapshot: loggedIn.user,
      });
      setElevatedUntil(Date.now() + ELEVATED_TTL_MS);
    } catch (err) {
      if (err instanceof ApiException) {
        if (err.isUnauthorized) {
          await handleRemoteAuthFailure(
            err.error.code,
            err.error.message || 'This device was removed. Sign in with Google to continue.'
          );
        }
        // Surface server verification errors (e.g. INVALID_ASSERTION) on the lock screen
        setLockMessage(err.error.message || err.message);
      }
      throw err;
    }
  }, [applyOnlineSession, handleRemoteAuthFailure]);

  const enrollFaceId = useCallback(async () => {
    if (!isStandaloneDisplay()) {
      throw new Error('Face ID unlock is only available in the installed app');
    }
    if (!getAccessToken()) {
      throw new Error('Sign in online before enabling Face ID');
    }

    const options = await webauthnApi.registerOptions();
    const { credential, prfOutput } = await enrollPlatformAuthenticator(options, {
      enablePrf: true,
    });
    const label = getAuthenticatorLabel();
    const saved = await webauthnApi.register(credential, label);

    let wrappedDek: string | undefined;
    let prfEnabled = false;
    if (prfOutput) {
      const dek = await generateDek();
      wrappedDek = await wrapDek(dek, prfOutput);
      setMemoryDek(dek);
      prfEnabled = true;
      const { migratePlaintextIntoSealedVault } = await import('@/lib/sealedOffline');
      await migratePlaintextIntoSealedVault();
    }

    const userId = user?.id || (await authApi.getMe()).userId;
    const record: Omit<DeviceUnlockRecord, 'id'> = {
      userId,
      // Prefer server-normalized credential id so login allowCredentials matches DB bytes.
      credentialId: saved.credentialId || credential.id,
      serverCredentialId: saved.id,
      enrolledAt: new Date().toISOString(),
      prfEnabled,
      wrappedDek,
      authenticatorLabel: label,
    };
    await deviceUnlockDb.save(record);
    deviceUnlockRef.current = { ...record, id: 'local' };
    setIsFaceIdEnrolled(true);
    setCanOfflineUnlock(prfEnabled);
    setShowFaceIdEnrollNudge(false);
    await metadataDb.set(ENROLL_NUDGE_DISMISS_KEY, '1');
    return { prfEnabled };
  }, [user?.id]);

  const disableFaceId = useCallback(async () => {
    if (!navigator.onLine) {
      throw new Error('You must be online to change security settings.');
    }

    const ok = await confirmWipeIfUnsynced();
    if (!ok) return;

    const record = await deviceUnlockDb.get();
    if (record?.serverCredentialId && getAccessToken()) {
      try {
        await webauthnApi.deleteCredential(record.serverCredentialId);
      } catch {
        /* still clear local */
      }
    }

    await clearVaultBlobs();
    await deviceUnlockDb.clear();
    wipeMemoryDek();
    deviceUnlockRef.current = null;
    setIsFaceIdEnrolled(false);
    setCanOfflineUnlock(false);
  }, []);

  const requireElevatedAuth = useCallback(async () => {
    if (!navigator.onLine) {
      return { ok: false, message: 'You must be online to change security settings.' };
    }

    if (Date.now() < elevatedUntil && sessionState === 'onlineAuthenticated') {
      return { ok: true };
    }

    if (isStandaloneDisplay() && isFaceIdEnrolled) {
      try {
        // Re-assert Face ID for elevation without full lock cycle when already online
        const record = deviceUnlockRef.current ?? (await deviceUnlockDb.get());
        if (!record) return { ok: false, message: 'Face ID is not set up.' };
        const options = await webauthnApi.loginOptions(record.credentialId);
        if (!options.allowCredentials?.length) {
          options.allowCredentials = [{ id: record.credentialId, type: 'public-key' }];
        }
        const { credential } = await assertPlatformAuthenticator(options, { enablePrf: false });
        const loggedIn = await webauthnApi.login(credential);
        setAccessToken(loggedIn.accessToken);
        setRefreshBlocked(false);
        setSessionState('onlineAuthenticated');
        if (loggedIn.user?.userId) {
          setUser({
            id: loggedIn.user.userId,
            email: loggedIn.user.email ?? '',
            username: loggedIn.user.username ?? '',
            displayName: loggedIn.user.displayName,
            name: loggedIn.user.name,
            provider: loggedIn.user.provider,
            providerManaged: loggedIn.user.providerManaged ?? false,
            isAdmin: loggedIn.user.isAdmin ?? false,
          });
        }
        setElevatedUntil(Date.now() + ELEVATED_TTL_MS);
        return { ok: true };
      } catch {
        return { ok: false, message: `${faceIdLabel} verification failed.` };
      }
    }

    // Web / non-enrolled PWA: Google session is sufficient
    if (sessionState === 'onlineAuthenticated' && getAccessToken()) {
      setElevatedUntil(Date.now() + ELEVATED_TTL_MS);
      return { ok: true };
    }

    return { ok: false, message: 'Sign in with Google to continue.' };
  }, [elevatedUntil, sessionState, isFaceIdEnrolled, faceIdLabel]);

  const dismissFaceIdEnrollNudge = useCallback(() => {
    setShowFaceIdEnrollNudge(false);
    void metadataDb.set(ENROLL_NUDGE_DISMISS_KEY, '1');
  }, []);

  const refreshUser = useCallback(async () => {
    const token = getAccessToken();
    if (token) {
      await loadUserFromToken(token);
    }
  }, [loadUserFromToken]);

  const isLocked = sessionState === 'locked';
  const effectiveAuthenticated =
    sessionState === 'onlineAuthenticated' ||
    sessionState === 'localUnlocked' ||
    (sessionState !== 'locked' && !!user && !!getAccessToken());

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: effectiveAuthenticated && !isLocked,
        isLoading,
        authError,
        clearAuthError,
        sessionState,
        isLocked,
        isFaceIdEnrolled,
        faceIdLabel,
        canOfflineUnlock,
        showFaceIdEnrollNudge,
        dismissFaceIdEnrollNudge,
        loginWithCognito,
        logout,
        getAccessToken: () => getAccessToken(),
        handleTokenExpiration,
        refreshUser,
        lockSession,
        unlockWithFaceId,
        enrollFaceId,
        disableFaceId,
        requireElevatedAuth,
        isElevated: Date.now() < elevatedUntil,
        lockMessage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
